import { useState, useEffect } from "react";
import axios from "axios";
import { ArrowLeft, Download, Users, MapPin, Mail, Filter, Lock, Trash2, Bell, Settings, RefreshCw, Briefcase } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// HAEVN Logo Component (SVG)
const HaevnLogo = ({ className = "", color = "#252627" }) => (
  <svg viewBox="0 0 1554.72 337.36" className={className} fill={color} data-testid="haevn-logo">
    <g><g>
      <path d="M48.51,97.71h109.87v44.85h19.21v-44.85h108.14c17.11,0,31.03,13.92,31.03,31.03s-13.92,31.03-31.03,31.03h-13.12v19.21h13.12c27.71,0,50.24-22.54,50.24-50.24s-22.54-50.24-50.24-50.24H48.51c-16.15,0-29.3-13.14-29.3-29.29v-.69c0-16.15,13.14-29.3,29.3-29.3s29.29,13.14,29.29,29.3v16.24h19.21v-16.24C97.01,21.76,75.25,0,48.51,0S0,21.76,0,48.51v.69c0,26.75,21.76,48.51,48.51,48.51Z"/>
      <path d="M287.46,239.66h-109.88v-46.23h-19.21v46.23H50.24c-17.11,0-31.03-13.92-31.03-31.03s13.92-31.03,31.03-31.03h13.12v-19.21h-13.12c-27.71,0-50.24,22.54-50.24,50.24s22.54,50.24,50.24,50.24h237.22c16.15,0,29.3,13.14,29.3,29.3v.69c0,16.15-13.14,29.3-29.3,29.3s-29.29-13.14-29.29-29.3v-16.24h-19.21v16.24c0,26.75,21.76,48.51,48.5,48.51s48.51-21.76,48.51-48.51v-.69c0-26.75-21.76-48.51-48.51-48.51Z"/>
      <path d="M177.59,49.2c0-16.54,13.45-29.99,29.99-29.99s29.99,13.45,29.99,29.99v15.55h19.21v-15.55c0-27.13-22.07-49.2-49.2-49.2s-49.2,22.07-49.2,49.2v15.55h19.21v-15.55Z"/>
      <path d="M158.38,288.16c0,16.54-13.45,29.99-29.99,29.99s-29.99-13.45-29.99-29.99v-15.55h-19.21v15.55c0,27.13,22.07,49.2,49.2,49.2s49.2-22.07,49.2-49.2v-15.55h-19.21v15.55Z"/>
      <polygon points="256.78 227.46 256.78 111.3 237.57 111.3 237.57 158.38 98.4 158.39 98.4 111.29 79.19 111.29 79.19 227.45 98.4 227.45 98.4 177.6 237.57 177.59 237.57 227.46 256.78 227.46"/>
    </g><g>
      <polygon points="597.4 151.71 477.49 151.71 477.49 53.9 445.95 53.9 445.95 283.47 477.49 283.47 477.49 182.45 597.4 182.45 597.4 283.47 629.1 283.47 629.1 53.9 597.4 53.9 597.4 151.71"/>
      <polygon points="1090.09 84.63 1078.12 53.9 922.51 53.9 922.51 283.47 1090.09 283.47 1090.09 252.57 954.04 252.57 954.04 179.25 1050.1 179.25 1050.1 148.67 954.04 148.67 954.04 84.47 1090.09 84.63"/>
      <polygon points="1221.82 239.28 1150.1 53.9 1117.44 53.9 1206.94 283.47 1236.55 283.47 1326.05 53.9 1293.23 53.9 1221.82 239.28"/>
      <polygon points="1523.34 53.9 1523.34 226.95 1390.3 53.9 1362.77 53.9 1362.77 283.47 1394.15 283.47 1394.15 110.41 1527.18 283.47 1554.72 283.47 1554.72 53.9 1523.34 53.9"/>
      <polygon points="761.87 53.9 665.18 283.47 699.12 283.47 775.96 98.72 852.33 283.47 886.43 283.47 789.73 53.9 761.87 53.9"/>
    </g></g>
  </svg>
);

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white border border-gray-100 p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-3 mb-2">
      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <span className="text-sm text-gray-500 font-body">{label}</span>
    </div>
    <p className="text-2xl font-headline text-haevn-navy ml-11">{value}</p>
  </div>
);

const DataTable = ({ columns, rows, emptyText }) => (
  <div className="overflow-x-auto" data-testid="data-table">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b-2 border-gray-100">
          {columns.map((col) => (
            <th key={col.key} className="text-left py-3 px-4 font-subhead text-gray-500 uppercase text-xs tracking-wider">{col.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={columns.length} className="py-8 text-center text-gray-400 font-body">{emptyText}</td></tr>
        ) : (
          rows.map((row, i) => (
            <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
              {columns.map((col) => (
                <td key={col.key} className="py-3 px-4 font-body text-gray-700">{col.render ? col.render(row[col.key], row) : row[col.key] || "\u2014"}</td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

// City Management Panel
function CityManagement({ storedPassword, onRefresh }) {
  const [allCities, setAllCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifying, setNotifying] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [notificationLog, setNotificationLog] = useState([]);

  useEffect(() => { fetchCities(); fetchNotifications(); }, []);

  const fetchCities = async () => {
    try {
      const res = await axios.get(`${API}/cities`);
      const all = [...(res.data.activated || []), ...(res.data.open || []), ...(res.data.on_deck || [])];
      setAllCities(all);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications?limit=20`);
      setNotificationLog(res.data);
    } catch (err) { console.error(err); }
  };

  const handleStatusChange = async (slug, newStatus) => {
    setUpdating(slug);
    try {
      await axios.put(`${API}/admin/city/${slug}/status`, { status: newStatus, password: storedPassword });
      await fetchCities();
      if (onRefresh) onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update status.");
    }
    setUpdating(null);
  };

  const handleNotify = async (slug) => {
    if (!window.confirm("Send activation notifications to all waitlist members for this city?")) return;
    setNotifying(slug);
    try {
      const res = await axios.post(`${API}/admin/city/${slug}/notify`, { password: storedPassword });
      alert(res.data.message);
      await fetchNotifications();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to send notifications.");
    }
    setNotifying(null);
  };

  const statusOptions = ["on-deck", "open", "activated"];
  const statusLabels = { "on-deck": "Coming Next", "open": "Now Building", "activated": "Live" };
  const statusColors = { "on-deck": "#9CA3AF", "open": "#E29E0C", "activated": "#008080" };

  if (loading) return <p className="text-gray-400 font-body">Loading cities...</p>;

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-100" data-testid="city-management-panel">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-gray-400" />
            <h2 className="font-subhead text-gray-900">City Status Management</h2>
          </div>
          <Button onClick={() => { fetchCities(); fetchNotifications(); }} className="bg-white border border-gray-200 text-gray-600 px-3 py-1.5 font-subhead text-xs hover:bg-gray-50 rounded-none h-auto" data-testid="refresh-cities-btn">
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
        </div>
        <div className="divide-y divide-gray-50">
          {allCities.map((city) => (
            <div key={city.slug} className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`manage-city-${city.slug}`}>
              <div className="flex items-center gap-3">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: statusColors[city.status] }} />
                <div>
                  <p className="font-subhead text-sm text-gray-900">{city.name}, {city.state}</p>
                  <p className="text-xs text-gray-400 font-body">{statusLabels[city.status]} | {city.opens_date || "TBD"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={city.status}
                  onChange={(e) => handleStatusChange(city.slug, e.target.value)}
                  disabled={updating === city.slug}
                  className="text-xs border border-gray-200 px-2 py-1.5 font-body bg-white text-gray-700 focus:outline-none focus:border-haevn-navy"
                  data-testid={`status-select-${city.slug}`}
                >
                  {statusOptions.map((s) => (
                    <option key={s} value={s}>{statusLabels[s]}</option>
                  ))}
                </select>
                <Button
                  onClick={() => handleNotify(city.slug)}
                  disabled={notifying === city.slug}
                  className="bg-haevn-teal/10 border border-haevn-teal/30 text-haevn-teal px-3 py-1.5 font-subhead text-xs hover:bg-haevn-teal/20 rounded-none h-auto"
                  data-testid={`notify-btn-${city.slug}`}
                >
                  <Bell className="w-3 h-3 mr-1" />
                  {notifying === city.slug ? "Sending..." : "Notify Waitlist"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Notification Log */}
      {notificationLog.length > 0 && (
        <div className="bg-white border border-gray-100" data-testid="notification-log">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-gray-400" />
              <h2 className="font-subhead text-gray-900">Recent Notifications</h2>
              <span className="text-xs bg-haevn-teal/10 text-haevn-teal px-2 py-0.5 font-subhead">QUEUED (email service not connected)</span>
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-gray-50">
            {notificationLog.slice(0, 20).map((n, i) => (
              <div key={i} className="p-3 flex items-center justify-between text-sm">
                <div>
                  <span className="text-gray-700 font-body">{n.email}</span>
                  <span className="text-xs text-gray-400 font-body ml-2">{n.city_name}</span>
                </div>
                <span className="text-xs text-gray-400 font-body">{new Date(n.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [storedPassword, setStoredPassword] = useState("");

  const [stats, setStats] = useState(null);
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [zipEntries, setZipEntries] = useState([]);
  const [generalEntries, setGeneralEntries] = useState([]);
  const [careersEntries, setCareersEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("waitlist");
  const [cityFilter, setCityFilter] = useState("");
  const [purging, setPurging] = useState(false);

  useEffect(() => {
    if (authenticated) fetchData();
  }, [cityFilter, authenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError(null);
    setAuthLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password });
      setStoredPassword(password);
      setAuthenticated(true);
    } catch {
      setAuthError("Invalid password.");
    } finally {
      setAuthLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const filterParam = cityFilter ? `?city_slug=${cityFilter}` : "";
      const [statsRes, waitlistRes, zipRes, generalRes, careersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/city-waitlists${filterParam}`),
        axios.get(`${API}/zip-interests`),
        axios.get(`${API}/admin/general-waitlist`),
        axios.get(`${API}/admin/careers-interest`),
      ]);
      setStats(statsRes.data);
      setWaitlistEntries(waitlistRes.data);
      setZipEntries(zipRes.data);
      setGeneralEntries(generalRes.data);
      setCareersEntries(careersRes.data);
    } catch (err) {
      console.error("Failed to fetch admin data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePurgeData = async () => {
    if (!window.confirm("Are you sure you want to delete ALL waitlist and ZIP interest data? This cannot be undone.")) return;
    setPurging(true);
    try {
      const res = await axios.post(`${API}/admin/purge-demo-data`, { password: storedPassword });
      alert(`Purged ${res.data.deleted.city_waitlists} waitlist entries and ${res.data.deleted.zip_interests} ZIP interests.`);
      fetchData();
    } catch {
      alert("Failed to purge data.");
    } finally {
      setPurging(false);
    }
  };

  const handleExportWaitlist = () => {
    const filterParam = cityFilter ? `?city_slug=${cityFilter}` : "";
    window.open(`${API}/admin/city-waitlists/export${filterParam}`, "_blank");
  };
  const handleExportZip = () => window.open(`${API}/admin/zip-interests/export`, "_blank");
  const handleExportGeneral = () => window.open(`${API}/admin/general-waitlist/export`, "_blank");

  const formatTimestamp = (ts) => {
    if (!ts) return "\u2014";
    const d = new Date(ts);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  };

  const waitlistColumns = [
    { key: "email", label: "Email" },
    { key: "city_name", label: "City" },
    { key: "zip_code", label: "ZIP" },
    { key: "phone", label: "Phone" },
    { key: "sms_opt_in", label: "SMS", render: (val) => val ? "Yes" : "No" },
    { key: "timestamp", label: "Signed Up", render: (val) => formatTimestamp(val) },
  ];

  const zipColumns = [
    { key: "email", label: "Email" },
    { key: "zip_code", label: "ZIP Code" },
    { key: "source", label: "Source" },
    { key: "timestamp", label: "Submitted", render: (val) => formatTimestamp(val) },
  ];

  const generalColumns = [
    { key: "email", label: "Email" },
    { key: "zip_code", label: "ZIP Code" },
    { key: "phone", label: "Phone" },
    { key: "sms_opt_in", label: "SMS", render: (val) => val ? "Yes" : "No" },
    { key: "timestamp", label: "Signed Up", render: (val) => formatTimestamp(val) },
  ];

  const careersColumns = [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "linkedin", label: "LinkedIn" },
    { key: "note", label: "Note" },
    { key: "timestamp", label: "Submitted", render: (val) => formatTimestamp(val) },
  ];

  const tabClass = (tab) =>
    `px-4 py-2 text-sm font-subhead transition-all duration-200 border-b-2 ${
      activeTab === tab ? "text-haevn-navy border-haevn-navy" : "text-gray-400 border-transparent hover:text-gray-600"
    }`;

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="admin-login">
        <div className="w-full max-w-sm mx-4">
          <div className="bg-white border border-gray-100 p-8">
            <div className="flex justify-center mb-6">
              <HaevnLogo className="h-7 w-auto" color="#1E2A4A" />
            </div>
            <div className="flex items-center gap-2 justify-center mb-6">
              <Lock className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-subhead text-gray-500 uppercase tracking-wider">Admin Access</span>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="bg-white border-2 border-gray-200 px-4 py-3 text-base font-body rounded-none placeholder:text-gray-400 focus:border-haevn-navy focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                data-testid="admin-password-input" autoFocus
              />
              {authError && <p className="text-sm text-red-500 font-body">{authError}</p>}
              <Button type="submit" disabled={authLoading} className="w-full bg-haevn-navy text-white px-4 py-3 font-subhead text-base hover:bg-haevn-navy/90 rounded-none h-auto" data-testid="admin-login-btn">
                {authLoading ? "Verifying..." : "Enter"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-page">
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100 py-4 px-6 md:px-12">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <a href="/" data-testid="admin-logo"><HaevnLogo className="h-6 w-auto" color="#1E2A4A" /></a>
            <span className="text-xs font-subhead text-haevn-gold uppercase tracking-wider border border-haevn-gold/30 px-2 py-0.5">Admin</span>
          </div>
          <a href="/" className="flex items-center gap-2 text-sm text-gray-500 hover:text-haevn-navy transition-colors font-body" data-testid="admin-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Site
          </a>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-headline text-2xl text-haevn-navy" data-testid="admin-title">Dashboard</h1>
          <Button onClick={handlePurgeData} disabled={purging} className="bg-white border border-red-200 text-red-500 px-4 py-2 font-subhead text-sm hover:bg-red-50 hover:border-red-300 rounded-none h-auto" data-testid="purge-data-btn">
            <Trash2 className="w-4 h-4 mr-2" /> {purging ? "Purging..." : "Purge All Data"}
          </Button>
        </div>

        {loading ? (
          <p className="text-gray-400 font-body">Loading...</p>
        ) : (
          <>
            {/* Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-testid="stats-row">
              <StatCard label="City Waitlists" value={stats?.total_waitlist_signups || 0} icon={Users} color="#E29E0C" />
              <StatCard label="General Waitlist" value={stats?.total_general_waitlist || 0} icon={Users} color="#1E2A4A" />
              <StatCard label="ZIP Interests" value={stats?.total_zip_interests || 0} icon={Mail} color="#008080" />
              <StatCard label="Cities Tracked" value={stats?.cities?.length || 0} icon={MapPin} color="#1E2A4A" />
              <StatCard label="Careers Interest" value={careersEntries.length} icon={Briefcase} color="#008080" />
            </div>

            {/* City Breakdown */}
            {stats?.cities && (
              <div className="bg-white border border-gray-100 p-5 mb-8" data-testid="city-breakdown">
                <h2 className="font-subhead text-gray-900 mb-4">Waitlist by City</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {stats.cities.map((c) => (
                    <button
                      key={c.slug}
                      onClick={() => { setCityFilter(cityFilter === c.slug ? "" : c.slug); setActiveTab("waitlist"); }}
                      className={`text-left p-3 border transition-all duration-200 ${cityFilter === c.slug ? "border-haevn-gold bg-haevn-gold/5" : "border-gray-100 hover:border-gray-200"}`}
                      data-testid={`city-filter-${c.slug}`}
                    >
                      <p className="font-subhead text-sm text-gray-900">{c.name}, {c.state}</p>
                      <p className="text-lg font-headline text-haevn-navy">{c.waitlist_count}</p>
                      <p className="text-xs text-gray-400 font-body">{c.status === "open" ? "Open" : "Pre-Launch"}</p>
                    </button>
                  ))}
                </div>
                {cityFilter && (
                  <button onClick={() => setCityFilter("")} className="mt-3 text-xs text-haevn-gold font-subhead hover:underline" data-testid="clear-filter">Clear filter</button>
                )}
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 mb-6 border-b border-gray-100 overflow-x-auto" data-testid="admin-tabs">
              <button className={tabClass("waitlist")} onClick={() => setActiveTab("waitlist")} data-testid="tab-waitlist">City Waitlists ({waitlistEntries.length})</button>
              <button className={tabClass("zip")} onClick={() => setActiveTab("zip")} data-testid="tab-zip">ZIP Interests ({zipEntries.length})</button>
              <button className={tabClass("general")} onClick={() => setActiveTab("general")} data-testid="tab-general">General ({generalEntries.length})</button>
              <button className={tabClass("careers")} onClick={() => setActiveTab("careers")} data-testid="tab-careers">Careers ({careersEntries.length})</button>
              <button className={tabClass("cities")} onClick={() => setActiveTab("cities")} data-testid="tab-cities">City Mgmt</button>
            </div>

            {/* Waitlist Tab */}
            {activeTab === "waitlist" && (
              <div className="bg-white border border-gray-100" data-testid="waitlist-panel">
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <h2 className="font-subhead text-gray-900">City Waitlist Signups</h2>
                    {cityFilter && (
                      <span className="text-xs bg-haevn-gold/10 text-haevn-gold px-2 py-0.5 font-subhead"><Filter className="w-3 h-3 inline mr-1" />{cityFilter}</span>
                    )}
                  </div>
                  <Button onClick={handleExportWaitlist} className="bg-haevn-navy text-white px-4 py-2 font-subhead text-sm hover:bg-haevn-navy/90 rounded-none h-auto" data-testid="export-waitlist-btn">
                    <Download className="w-4 h-4 mr-2" /> Export CSV
                  </Button>
                </div>
                <DataTable columns={waitlistColumns} rows={waitlistEntries} emptyText="No waitlist signups yet." />
              </div>
            )}

            {/* ZIP Interest Tab */}
            {activeTab === "zip" && (
              <div className="bg-white border border-gray-100" data-testid="zip-panel">
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                  <h2 className="font-subhead text-gray-900">ZIP Code Interests</h2>
                  <Button onClick={handleExportZip} className="bg-haevn-navy text-white px-4 py-2 font-subhead text-sm hover:bg-haevn-navy/90 rounded-none h-auto" data-testid="export-zip-btn">
                    <Download className="w-4 h-4 mr-2" /> Export CSV
                  </Button>
                </div>
                <DataTable columns={zipColumns} rows={zipEntries} emptyText="No ZIP interest submissions yet." />
              </div>
            )}

            {/* General Waitlist Tab */}
            {activeTab === "general" && (
              <div className="bg-white border border-gray-100" data-testid="general-panel">
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                  <h2 className="font-subhead text-gray-900">General Waitlist Signups</h2>
                  <Button onClick={handleExportGeneral} className="bg-haevn-navy text-white px-4 py-2 font-subhead text-sm hover:bg-haevn-navy/90 rounded-none h-auto" data-testid="export-general-btn">
                    <Download className="w-4 h-4 mr-2" /> Export CSV
                  </Button>
                </div>
                <DataTable columns={generalColumns} rows={generalEntries} emptyText="No general waitlist signups yet." />
              </div>
            )}

            {/* Careers Interest Tab */}
            {activeTab === "careers" && (
              <div className="bg-white border border-gray-100" data-testid="careers-panel">
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                  <h2 className="font-subhead text-gray-900">Careers Interest</h2>
                </div>
                <DataTable columns={careersColumns} rows={careersEntries} emptyText="No careers submissions yet." />
              </div>
            )}

            {/* City Management Tab */}
            {activeTab === "cities" && (
              <CityManagement storedPassword={storedPassword} onRefresh={fetchData} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
